import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CopyIcon } from "@chakra-ui/icons";
import { CustomToast, Dropdown, FormInput } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { refreshHistory } from "../../components/History/modal";
import {
  defaultValues,
  IMeeting,
  IMeetingForm,
  isValidEmail,
  isValidUrl,
  meetingDurationOptions,
  meetingSchema,
  statusTypeOptions,
  StatusType,
} from "../../views/Leads/components/MeetingFormTab/model";

interface MeetingEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number;
  meetingId: number;
  onMeetingSaved?: () => void;
}

const MeetingEditModal = ({
  isOpen,
  onClose,
  leadId,
  meetingId,
  onMeetingSaved,
}: MeetingEditModalProps) => {
  const { addToast } = CustomToast();
  const { fetchApi, loading: apiLoading } = useFetch(addToast);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [originalStatus, setOriginalStatus] = useState<StatusType | null>(null);
  const [isReadOnlyMeeting, setIsReadOnlyMeeting] = useState(false);
  const [meetings, setMeetings] = useState<IMeeting[]>([]);
  const [locations, setLocations] = useState<{ id: number; name: string; locationName: string }[]>([]);
  const today = new Date().toISOString().split("T")[0];

  const [includeSignature, setIncludeSignature] = useState(true);
  const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
  const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

  const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
  const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string | null>(null);
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);

  const loadLocations = async () => {
    try {
      const res = await fetchApi("Dropdown/getLocation", "GET");
      const rawList = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
        ? (res as any).data
        : Array.isArray((res as any)?.locations)
        ? (res as any).locations
        : Array.isArray((res as any)?.result)
        ? (res as any).result
        : [];

      if (rawList && rawList.length > 0) {
        const formatted = rawList.map((x: any) => {
          const idVal = x.id ?? x.Id ?? x.locationId ?? x.LocationId ?? x.value;
          const nameVal = x.locationName ?? x.LocationName ?? x.name ?? x.Name ?? x.text ?? x.title ?? "";
          return {
            id: Number(idVal),
            name: String(nameVal),
            locationName: String(nameVal),
            locationId: Number(idVal),
          };
        });
        setLocations(formatted);
      }
    } catch (e) {
      console.error("Error loading locations:", e);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    const fetchSignature = async () => {
      if (includeSignature && !userSignature && !loadingSignature) {
        setLoadingSignature(true);
        try {
          const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
          if (res) {
            setUserSignature({
              shortSignature: res.shortSignature || "",
              fullSignature: res.fullSignature || "",
            });
          }
        } catch (error) {
          console.error("Error fetching signature:", error);
        } finally {
          setLoadingSignature(false);
        }
      }
    };
    fetchSignature();
  }, [includeSignature, userSignature, loadingSignature]);

  useEffect(() => {
    const fetchDisclaimer = async () => {
      if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
        setLoadingDisclaimer(true);
        try {
          const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
          if (res) {
            setWebsiteDisclaimer(res.disclaimer || "");
          }
        } catch (error) {
          console.error("Error fetching disclaimer:", error);
        } finally {
          setLoadingDisclaimer(false);
        }
      }
    };
    fetchDisclaimer();
  }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IMeetingForm>({
    resolver: yupResolver(meetingSchema),
    defaultValues,
  });

  const isAllDay = watch("isAllDay");
  const selectedMeetingLinkType = watch("meetingLinkType");
  const startTime = watch("startDateTime");
  const meetingDuration = watch("meetingDuration");

  const meetingLinkTypeOptions = isAllDay
    ? [
      { id: "usingLink", name: "Using Link" },
      { id: "locationVisit", name: "Location Visit" },
    ]
    : [
      { id: "googleLink", name: "Google Link" },
      { id: "usingLink", name: "Using Link" },
      { id: "locationVisit", name: "Location Visit" },
    ];

  const getStatusOptions = (currentStatus?: StatusType) => {
    switch (currentStatus) {
      case StatusType.Rescheduled:
        return statusTypeOptions.filter((x) => x.id !== StatusType.Scheduled);
      case StatusType.Done:
        return [{ id: StatusType.Done, name: "Done" }];
      case StatusType.Cancelled:
        return [{ id: StatusType.Cancelled, name: "Cancelled" }];
      default:
        return statusTypeOptions;
    }
  };

  const handleCopyLink = (link: string) => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    addToast({
      message: "Meeting link copied successfully!",
      status: "success",
    });
  };

  const isMeetingLinkActive = (row: IMeeting) => {
    if (row.meetingStatusType !== StatusType.Scheduled && row.meetingStatusType !== StatusType.Rescheduled) {
      return false;
    }
    if (!row.meeting_Link || !isValidUrl(row.meeting_Link)) return false;
    const now = new Date();
    if (row.isAllDay) {
      const todayStr = now.toLocaleDateString("en-CA");
      const meetingDateStr = row.meetingDate ? row.meetingDate.split("T")[0] : "";
      return todayStr === meetingDateStr;
    }
    if (!row.startDateTime || !row.endDateTime) return false;
    const start = new Date(
      row.startDateTime.endsWith("Z") ? row.startDateTime : `${row.startDateTime}Z`
    );
    const end = new Date(
      row.endDateTime.endsWith("Z") ? row.endDateTime : `${row.endDateTime}Z`
    );
    return now >= start && now <= end;
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
      if (!trimmed) return;
      if (!isValidEmail(trimmed)) {
        setEmailError("Invalid email address.");
        return;
      }
      if (emailList.includes(trimmed)) {
        setEmailError("Email already added.");
        return;
      }
      setEmailList((prev) => [...prev, trimmed]);
      setEmailInput("");
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setEmailError("Invalid email address.");
      return;
    }
    if (emailList.includes(trimmed)) {
      setEmailError("Email already added.");
      return;
    }
    setEmailList((prev) => [...prev, trimmed]);
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (email: string) =>
    setEmailList((prev) => prev.filter((e) => e !== email));

  const loadMeetingDetails = async () => {
    try {
      setLoadingDetails(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await fetchApi(
        `LeadRequirement/LeadMeeting?leadId=${leadId}&filter=all&status=all&timeZone=${encodeURIComponent(
          timezone
        )}`,
        "GET"
      );

      if (Array.isArray(res?.data)) {
        setMeetings(res.data);
        const match = res.data.find((m: any) => m.id === meetingId);
        if (match) {
          setOriginalStatus(match.meetingStatusType);
          setIsReadOnlyMeeting(
            match.meetingStatusType === StatusType.Done ||
            match.meetingStatusType === StatusType.Cancelled
          );
          setEmailList(match.extraEmails ?? []);
          setIncludeSignature(match.includeSignature ?? match.IncludeSignature ?? true);
          setSignatureType(match.signatureType ?? match.SignatureType ?? "Full");
          setIncludeDisclaimer(match.includeDisclaimer ?? match.IncludeDisclaimer ?? true);
          reset({
            ...match,
            locationId: match.locationId ?? match.LocationId ?? undefined,
            meetingDate: match.isAllDay
              ? match.meetingDate?.split("T")[0] || ""
              : match.startDateTime
                ? new Date(`${match.startDateTime}Z`).toLocaleDateString("en-CA")
                : "",
            startDateTime: match.isAllDay
              ? ""
              : match.startDateTime
                ? new Date(`${match.startDateTime}Z`).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                : "",
            endDateTime: match.isAllDay
              ? ""
              : match.endDateTime
                ? new Date(`${match.endDateTime}Z`).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                : "",
            remarks: match.remarks || "",
            meetingLinkType: match.meetingLinkType ?? (match.locationId ? "locationVisit" : "googleLink"),
            meeting_Link:
              match.meetingLinkType === "usingLink" ? match.meeting_Link || "" : "",
          });
        } else {
          addToast({
            message: "Meeting details not found.",
            status: "error",
          });
          onClose();
        }
      } else {
        addToast({
          message: "Failed to load meeting details.",
          status: "error",
        });
        onClose();
      }
    } catch (err) {
      console.error(err);
      onClose();
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen && leadId && meetingId) {
      loadMeetingDetails();
    }
  }, [isOpen, leadId, meetingId]);

  useEffect(() => {
    if (isAllDay) {
      setValue("startDateTime", "");
      setValue("endDateTime", "");
      setValue("meetingDuration", 0);
      if (selectedMeetingLinkType === "googleLink") {
        setValue("meetingLinkType", "usingLink");
      }
    }
  }, [isAllDay, setValue, selectedMeetingLinkType]);

  useEffect(() => {
    if (startTime && meetingDuration) {
      const start = new Date(`2000-01-01T${startTime}`);
      start.setMinutes(start.getMinutes() + Number(meetingDuration));
      const hh = String(start.getHours()).padStart(2, "0");
      const mm = String(start.getMinutes()).padStart(2, "0");
      setValue("endDateTime", `${hh}:${mm}`);
    }
  }, [startTime, meetingDuration, setValue]);

  const onSubmit = async (data: IMeetingForm) => {
    const selectedDateTime = data.isAllDay ? new Date(`${data.meetingDate}T23:59:59`) : new Date(`${data.meetingDate}T${data.startDateTime}`);
    const now = new Date();
    if (
      data.meetingStatusType === StatusType.Rescheduled &&
      selectedDateTime <= now
    ) {
      addToast({
        message: "Rescheduled meeting must be scheduled for a future date and time.",
        status: "warning",
      });
      return;
    }
    let finalEmails = [...emailList];
    const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
    if (trimmed) {
      if (isValidEmail(trimmed)) {
        if (!finalEmails.includes(trimmed)) {
          finalEmails.push(trimmed);
          setEmailList((prev) =>
            prev.includes(trimmed) ? prev : [...prev, trimmed]
          );
          setEmailInput("");
          setEmailError("");
        }
      } else {
        setEmailError("Invalid email address.");
        return;
      }
    }

    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const meetingStatusName =
      statusTypeOptions.find((x) => x.id === data.meetingStatusType)?.name ?? "";

    const payload = {
      leadId,
      ...data,
      locationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
      LocationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
      meetingStatusName,
      meetingDuration: Number(data.meetingDuration),
      startDateTime:
        !data.isAllDay && data.startDateTime
          ? new Date(`${data.meetingDate}T${data.startDateTime}`).toISOString()
          : null,
      endDateTime:
        !data.isAllDay && data.endDateTime
          ? new Date(`${data.meetingDate}T${data.endDateTime}`).toISOString()
          : null,
      extraEmails: finalEmails,
      timeZoneId: timeZoneId,
      meetingLinkType: data.meetingLinkType,
      meeting_Link:
        data.meetingLinkType === "googleLink" ? "googleLink" : data.meetingLinkType === "locationVisit" ? "" : data.meeting_Link,
      IncludeSignature: includeSignature,
      IncludeDisclaimer: includeDisclaimer,
      SignatureType: signatureType,
      includeSignature: includeSignature,
      includeDisclaimer: includeDisclaimer,
      signatureType: signatureType,
    };

    const submitRequest = async (showSuccessMsg: boolean) => {
      const msg = showSuccessMsg ? "Meeting Updated Successfully" : undefined;
      return await fetchApi(
        `LeadRequirement/LeadMeeting/${meetingId}`,
        "PUT",
        payload,
        null,
        msg
      );
    };

    const res = await submitRequest(false);

    if (res && res.requiresAuth && res.authUrl) {
      const width = 500;
      const height = 650;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        res.authUrl,
        "google-oauth-popup",
        `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
      );

      if (popup) {
        const handleOAuthMessage = async (event: MessageEvent) => {
          if (event.data === "google-connected") {
            window.removeEventListener("message", handleOAuthMessage);
            addToast({
              message: "Google account connected! Saving meeting...",
              status: "info",
            });
            const retryRes = await submitRequest(true);
            if (retryRes && !retryRes.requiresAuth) {
              if (onMeetingSaved) onMeetingSaved();
              refreshHistory("Lead", leadId);
              onClose();
            }
          }
        };
        window.addEventListener("message", handleOAuthMessage);
      } else {
        addToast({
          message: "Popup blocked. Please allow popups to connect Google Calendar.",
          status: "warning",
        });
      }
      return;
    }

    if (res) {
      addToast({
        message: "Meeting Updated Successfully",
        status: "success",
      });
      if (onMeetingSaved) onMeetingSaved();
      refreshHistory("Lead", leadId);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <ModalHeader>Edit Meeting</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="65vh" overflowY="auto">
            {loadingDetails ? (
              <Flex justify="center" py={10}>
                <Spinner thickness="2px" speed="0.65s" color="#4048D6" size="lg" />
              </Flex>
            ) : (
              <Grid templateColumns="repeat(2,1fr)" gap={4}>
                <GridItem colSpan={2}>
                  <Controller
                    name="isAllDay"
                    control={control}
                    render={({ field }) => (
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">All Day</FormLabel>
                        <Switch
                          isChecked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          isDisabled={isReadOnlyMeeting}
                          colorScheme="green"
                        />
                      </FormControl>
                    )}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <FormControl isInvalid={!!emailError}>
                    <FormLabel className="font-poppins text_medium text_md">
                      Invite via Email
                    </FormLabel>
                    {emailList.length > 0 && (
                      <Flex
                        flexWrap="wrap"
                        gap={2}
                        mb={2}
                        p={2}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="gray.50"
                      >
                        {emailList.map((email) => (
                          <Flex
                            key={email}
                            align="center"
                            gap={1}
                            bg="#EEF0FD"
                            color="#4048D6"
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontSize="sm"
                            fontWeight="500"
                          >
                            <Text>{email}</Text>
                            <Text
                              cursor="pointer"
                              fontWeight="bold"
                              ml={1}
                              onClick={() => removeEmail(email)}
                              _hover={{ color: "red.500" }}
                            >
                              ×
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                    <Input
                      placeholder="Type email and press Enter or , to add"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError("");
                      }}
                      onKeyDown={handleEmailKeyDown}
                      onBlur={handleEmailBlur}
                      bg="white"
                      isDisabled={isReadOnlyMeeting}
                    />
                    {emailError && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {emailError}
                      </Text>
                    )}
                    <Text color="gray.400" fontSize="xs" mt={1}>
                      Press <strong>Enter</strong> or <strong>,</strong> to add multiple emails
                    </Text>
                  </FormControl>
                </GridItem>

                <GridItem colSpan={2}>
                  <FormInput
                    name="meetingTitle"
                    control={control}
                    errors={errors}
                    label="Title"
                    isRequired
                    disable={isReadOnlyMeeting}
                  />
                </GridItem>

                <GridItem>
                  <FormInput
                    isRequired
                    name="meetingDate"
                    control={control}
                    errors={errors}
                    label="Date"
                    type="date"
                    min={today}
                    disable={isReadOnlyMeeting}
                  />
                </GridItem>

                <GridItem>
                  <Dropdown
                    control={control}
                    name="meetingDuration"
                    label="Duration"
                    options={meetingDurationOptions}
                    valueKey="id"
                    labelKey="name"
                    errors={errors}
                    disable={isReadOnlyMeeting}
                  />
                </GridItem>

                <FormInput
                  name="startDateTime"
                  control={control}
                  errors={errors}
                  label="Start Time"
                  type="time"
                  disable={isAllDay || isReadOnlyMeeting}
                />

                <FormInput
                  name="endDateTime"
                  control={control}
                  label="End Time"
                  type="time"
                  disable={true}
                />

                {originalStatus && (
                  <GridItem>
                    <Dropdown
                      control={control}
                      name="meetingStatusType"
                      label="Status"
                      options={getStatusOptions(originalStatus)}
                      valueKey="id"
                      labelKey="name"
                      errors={errors}
                      disable={
                        originalStatus === StatusType.Done ||
                        originalStatus === StatusType.Cancelled
                      }
                    />
                  </GridItem>
                )}

                {originalStatus && (
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel className="font_poppins text_md text_medium">
                        Remarks
                      </FormLabel>
                      <Textarea
                        placeholder="Add Remarks"
                        bg="white"
                        className="font_poppins text_regular text_sm"
                        {...register("remarks")}
                        isDisabled={isReadOnlyMeeting}
                      />
                    </FormControl>
                  </GridItem>
                )}

                <GridItem>
                  <Dropdown
                    control={control}
                    name="meetingLinkType"
                    label="Meeting Type"
                    options={meetingLinkTypeOptions}
                    valueKey="id"
                    labelKey="name"
                    errors={errors}
                    disable={isReadOnlyMeeting}
                  />
                </GridItem>

                {selectedMeetingLinkType === "usingLink" && (
                  <GridItem>
                    <FormInput
                      name="meeting_Link"
                      control={control}
                      errors={errors}
                      label="Meeting Link"
                      placeholder="Enter meeting URL"
                      disable={isReadOnlyMeeting}
                    />
                  </GridItem>
                )}

                {selectedMeetingLinkType === "locationVisit" && (
                  <GridItem>
                    <Dropdown
                      isRequired
                      control={control}
                      name="locationId"
                      label="Location"
                      placeholder="Select Location"
                      options={locations}
                      valueKey="id"
                      labelKey="name"
                      errors={errors}
                      disable={isReadOnlyMeeting}
                    />
                  </GridItem>
                )}

                {watch("meeting_Link") && isValidUrl(watch("meeting_Link")) && (() => {
                  const currentMeeting = meetings.find((m) => m.id === meetingId);
                  const active = currentMeeting
                    ? isMeetingLinkActive(currentMeeting)
                    : false;
                  return (
                    <GridItem colSpan={2}>
                      <FormControl>
                        <FormLabel className="font-poppins text_medium text_md">
                          Joining Link {!active && (
                            <Text as="span" color="gray.400" fontSize="sm">
                              (Inactive until meeting starts)
                            </Text>
                          )}
                        </FormLabel>
                        <Flex
                          align="center"
                          gap={3}
                          p={3}
                          bg={active ? "#EEF0FD" : "gray.50"}
                          borderRadius="8px"
                          border="1px solid"
                          borderColor={active ? "#C7D7FE" : "gray.200"}
                        >
                          {active ? (
                            <Text
                              as="a"
                              href={watch("meeting_Link")}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="#4048D6"
                              fontWeight="600"
                              textDecoration="underline"
                              isTruncated
                              flex="1"
                            >
                              {watch("meeting_Link")}
                            </Text>
                          ) : (
                            <Text
                              color="gray.400"
                              fontWeight="500"
                              cursor="not-allowed"
                              isTruncated
                              flex="1"
                              title="Link will be active during the scheduled meeting time"
                            >
                              {watch("meeting_Link")} (Inactive)
                            </Text>
                          )}
                          <Button
                            size="sm"
                            leftIcon={<CopyIcon />}
                            onClick={() => {
                              if (active) handleCopyLink(watch("meeting_Link")!);
                            }}
                            isDisabled={!active}
                            bg={active ? "#4048D6" : "gray.200"}
                            color={active ? "white" : "gray.400"}
                            _hover={active ? { bg: "#353CC4" } : undefined}
                          >
                            Copy Link
                          </Button>
                        </Flex>
                      </FormControl>
                    </GridItem>
                  );
                })()}

                <GridItem colSpan={2}>
                  <FormControl isInvalid={!!errors.description} isRequired>
                    <FormLabel className="font-poppins text_medium text_md">
                      Description
                    </FormLabel>
                    <Textarea
                      placeholder="Add Description"
                      bg="white"
                      {...register("description")}
                      isDisabled={isReadOnlyMeeting}
                    />
                    {errors.description && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.description.message}
                      </Text>
                    )}
                  </FormControl>
                </GridItem>

                <GridItem colSpan={2}>
                  <FormControl>
                    <FormLabel>Email Options</FormLabel>

                    <Flex direction="column" gap={4}>
                      <Box>
                        <Checkbox
                          isChecked={includeSignature}
                          onChange={(e) => setIncludeSignature(e.target.checked)}
                          isDisabled={isReadOnlyMeeting}
                        >
                          Show Signature
                        </Checkbox>
                        {includeSignature && (
                          <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                            <RadioGroup
                              onChange={(val) => setSignatureType(val as "Short" | "Full")}
                              value={signatureType}
                            >
                              <Flex gap={4} mb={2}>
                                <Radio value="Short" colorScheme="blue" isDisabled={isReadOnlyMeeting}>
                                  Short Signature
                                </Radio>
                                <Radio value="Full" colorScheme="blue" isDisabled={isReadOnlyMeeting}>
                                  Full Signature
                                </Radio>
                              </Flex>
                            </RadioGroup>
                            {loadingSignature ? (
                              <Flex align="center" gap={2} mt={2}>
                                <Spinner size="xs" color="#4048D6" />
                                <Text fontSize="xs" color="gray.500">
                                  Loading signature...
                                </Text>
                              </Flex>
                            ) : (
                              <Box
                                mt={2}
                                p={3}
                                bg="gray.50"
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                maxH="150px"
                                overflowY="auto"
                                fontSize="sm"
                              >
                                {signatureType === "Short" ? (
                                  userSignature?.shortSignature ? (
                                    <Box
                                      dangerouslySetInnerHTML={{
                                        __html: userSignature.shortSignature,
                                      }}
                                    />
                                  ) : (
                                    <Text color="gray.400" fontStyle="italic">
                                      No short signature configured.
                                    </Text>
                                  )
                                ) : userSignature?.fullSignature ? (
                                  <Box
                                    dangerouslySetInnerHTML={{
                                      __html: userSignature.fullSignature,
                                    }}
                                  />
                                ) : (
                                  <Text color="gray.400" fontStyle="italic">
                                    No full signature configured.
                                  </Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Box>
                        <Checkbox
                          isChecked={includeDisclaimer}
                          onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                          isDisabled={isReadOnlyMeeting}
                        >
                          Show Disclaimer
                        </Checkbox>
                        {includeDisclaimer && (
                          <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                            {loadingDisclaimer ? (
                              <Flex align="center" gap={2}>
                                <Spinner size="xs" color="#4048D6" />
                                <Text fontSize="xs" color="gray.500">
                                  Loading disclaimer...
                                </Text>
                              </Flex>
                            ) : (
                              <Box
                                p={3}
                                bg="gray.50"
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                maxH="150px"
                                overflowY="auto"
                                fontSize="sm"
                              >
                                {websiteDisclaimer ? (
                                  <Box
                                    dangerouslySetInnerHTML={{
                                      __html: websiteDisclaimer,
                                    }}
                                  />
                                ) : (
                                  <Text color="gray.400" fontStyle="italic">
                                    No disclaimer configured.
                                  </Text>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Flex>
                  </FormControl>
                </GridItem>
              </Grid>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#4048D6"
              color="white"
              type="submit"
              isLoading={apiLoading}
              isDisabled={isReadOnlyMeeting || loadingDetails}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default MeetingEditModal;
